import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, getDocs, query, orderBy } from "firebase/firestore";

const RSVP_COLLECTION = "baptism_rsvps";
const G = "#c9a55a";
const NAVY = "#12122a";

const exportToCSV = (rsvps) => {
  if (rsvps.length === 0) return;
  const headers = ["Name", "Email", "Attending", "Session", "Guests", "Allergies", "Message", "Submitted At"];
  const rows = rsvps.map((r) => [
    r.name,
    r.email || "",
    r.attending,
    r.attending === "no" ? "" : r.session || "both",
    r.attending === "no" ? 0 : r.guests,
    (r.allergy || "").replace(/,/g, ";"),
    (r.message || "").replace(/,/g, ";"),
    new Date(r.submittedAt).toLocaleString(),
  ]);
  const csv = [headers, ...rows].map((row) => row.join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `baptism_rsvps_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

function StatCard({ label, value, color }) {
  return (
    <div
      style={{ background: "rgba(255,255,255,0.9)", border: `1px solid ${G}30` }}
      className="rounded-2xl p-5 flex flex-col items-center gap-1 shadow-sm"
    >
      <span style={{ color, fontFamily: "'Playfair Display', serif" }} className="text-3xl font-bold">
        {value}
      </span>
      <span style={{ color: "#9a8060", letterSpacing: "0.12em" }} className="text-xs uppercase">
        {label}
      </span>
    </div>
  );
}

export default function BaptismRecords() {
  const [rsvps, setRsvps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [copiedId, setCopiedId] = useState(null);

  const copyMessage = (id, text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  useEffect(() => {
    if (!document.getElementById("baptism-fonts")) {
      const link = document.createElement("link");
      link.id = "baptism-fonts";
      link.rel = "stylesheet";
      link.href =
        "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&display=swap";
      document.head.appendChild(link);
    }

    const fetchRSVPs = async () => {
      try {
        const q = query(collection(db, RSVP_COLLECTION), orderBy("submittedAt", "asc"));
        const snapshot = await getDocs(q);
        setRsvps(
          snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
            submittedAt: doc.data().submittedAt?.toDate?.().toISOString() ?? new Date().toISOString(),
          }))
        );
      } catch (err) {
        console.error("Failed to load RSVPs:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchRSVPs();
  }, []);

  const attending = rsvps.filter((r) => r.attending === "yes");
  const declining = rsvps.filter((r) => r.attending === "no");
  const maybe = rsvps.filter((r) => r.attending === "maybe");
  const totalGuests = attending.reduce((sum, r) => sum + Number(r.guests || 1), 0);

  const filtered = rsvps
    .filter((r) => filter === "all" || r.attending === filter)
    .filter((r) =>
      search === "" ||
      r.name?.toLowerCase().includes(search.toLowerCase()) ||
      r.email?.toLowerCase().includes(search.toLowerCase())
    );

  return (
    <div
      style={{
        background: "linear-gradient(160deg,#fdf8ef 0%,#fffcf6 50%,#fdf5e6 100%)",
        minHeight: "100vh",
        fontFamily: "'Cormorant Garamond', Georgia, serif",
      }}
    >
      <div style={{ height: 3, background: `linear-gradient(90deg,transparent,${G} 30%,${G} 70%,transparent)` }} />

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-10">
          <p style={{ letterSpacing: "0.25em", color: G }} className="text-xs uppercase mb-3">
            Munachimso Zoe Maxwell · Baptism
          </p>
          <h1 style={{ fontFamily: "'Playfair Display', serif", color: NAVY }} className="text-4xl font-bold mb-1">
            RSVP Records
          </h1>
          <div className="flex items-center justify-center gap-3 mt-4 max-w-xs mx-auto">
            <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg,transparent,${G}50)` }} />
            <span style={{ color: G }}>✦</span>
            <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg,${G}50,transparent)` }} />
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          <StatCard label="Attending" value={attending.length} color="#16a34a" />
          <StatCard label="Declining" value={declining.length} color="#dc2626" />
          <StatCard label="Maybe" value={maybe.length} color="#d97706" />
          <StatCard label="Total Guests" value={totalGuests} color={G} />
        </div>

        {/* Controls */}
        <div className="flex flex-wrap gap-3 mb-6 items-center justify-between">
          <div className="flex gap-2 flex-wrap">
            {[
              { value: "all", label: "All" },
              { value: "yes", label: "✓ Attending" },
              { value: "maybe", label: "? Maybe" },
              { value: "no", label: "✗ Declining" },
            ].map((opt) => (
              <button
                key={opt.value}
                onClick={() => setFilter(opt.value)}
                style={{
                  border: `1px solid ${filter === opt.value ? G : `${G}40`}`,
                  background: filter === opt.value ? `${G}18` : "rgba(255,255,255,0.7)",
                  color: filter === opt.value ? "#7a4f00" : "#9a8060",
                  fontFamily: "'Cormorant Garamond', serif",
                }}
                className="text-sm px-4 py-2 rounded-xl transition-all"
              >
                {opt.label}
              </button>
            ))}
          </div>

          <div className="flex gap-3 flex-wrap">
            <input
              type="text"
              placeholder="Search name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                borderColor: `${G}40`,
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: "0.95rem",
              }}
              className="border rounded-xl px-4 py-2 focus:outline-none bg-white/70 w-52"
              onFocus={(e) => (e.target.style.borderColor = G)}
              onBlur={(e) => (e.target.style.borderColor = `${G}40`)}
            />
            <button
              onClick={() => exportToCSV(rsvps)}
              disabled={rsvps.length === 0}
              style={{ background: `linear-gradient(135deg,#c9a55a,#a07838)` }}
              className="text-white text-sm font-semibold px-5 py-2 rounded-xl disabled:opacity-40 hover:opacity-90 transition-opacity"
            >
              ↓ Export CSV
            </button>
          </div>
        </div>

        {/* Records */}
        {loading ? (
          <div className="text-center py-20">
            <p style={{ color: G, fontFamily: "'Playfair Display', serif" }} className="text-xl italic">
              Loading records…
            </p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <p style={{ color: "#9a8060" }} className="text-base">
              No records found.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((r, i) => (
              <div
                key={r.id}
                style={{ background: "rgba(255,255,255,0.92)", border: `1px solid ${G}25` }}
                className="rounded-2xl p-5 shadow-sm flex flex-wrap gap-4 items-start"
              >
                {/* Index */}
                <div
                  style={{ color: `${G}80`, fontFamily: "'Playfair Display', serif" }}
                  className="text-sm font-semibold w-6 pt-0.5 shrink-0"
                >
                  {i + 1}
                </div>

                {/* Main info */}
                <div className="flex-1 min-w-0">
                  <p style={{ color: NAVY, fontFamily: "'Playfair Display', serif" }} className="font-semibold text-base">
                    {r.name}
                  </p>
                  {r.email && (
                    <p style={{ color: "#9a8060" }} className="text-sm mt-0.5">
                      {r.email}
                    </p>
                  )}
                  {r.allergy && (
                    <p style={{ color: "#b45309" }} className="text-xs mt-1.5">
                      Allergy: {r.allergy}
                    </p>
                  )}
                  {r.message && (
                    <div className="flex items-start gap-2 mt-2">
                      <p style={{ color: "#7a6645", fontStyle: "italic" }} className="text-sm">
                        "{r.message}"
                      </p>
                      <button
                        onClick={() => copyMessage(r.id, r.message)}
                        title="Copy message"
                        style={{ color: copiedId === r.id ? "#16a34a" : `${G}80` }}
                        className="shrink-0 mt-0.5 hover:opacity-70 transition-all"
                      >
                        {copiedId === r.id ? (
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                          </svg>
                        )}
                      </button>
                    </div>
                  )}
                </div>

                {/* Right side */}
                <div className="text-right shrink-0 flex flex-col items-end gap-1.5">
                  <span
                    className={`text-xs font-semibold px-3 py-1 rounded-full ${
                      r.attending === "yes"
                        ? "bg-green-100 text-green-700"
                        : r.attending === "no"
                        ? "bg-red-100 text-red-600"
                        : "bg-amber-100 text-amber-700"
                    }`}
                  >
                    {r.attending === "yes"
                      ? `✓ Yes · ${r.guests} guest${Number(r.guests) > 1 ? "s" : ""}`
                      : r.attending === "no"
                      ? "✗ Not attending"
                      : "? Maybe"}
                  </span>
                  {r.attending !== "no" && (
                    <p style={{ color: "#9a8060" }} className="text-xs">
                      {r.session === "church" ? "Church only" : r.session === "party" ? "Party only" : "Church & Party"}
                    </p>
                  )}
                  <p style={{ color: "#bba880" }} className="text-xs">
                    {new Date(r.submittedAt).toLocaleDateString("en-GB", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        <p style={{ color: `${G}60` }} className="text-center text-xs mt-8">
          {filtered.length} of {rsvps.length} records
        </p>
      </div>

      <div style={{ height: 3, background: `linear-gradient(90deg,transparent,${G} 30%,${G} 70%,transparent)` }} />
    </div>
  );
}
